/**
 * Read a video's actual codec out of an ISOBMFF (`.mp4` / `.mov`) container.
 *
 * Why this exists (#22523): Cloudinary silently transcoded HEVC to H.264 on delivery, so nobody
 * noticed that the S3 path has no transcode. Phones record HEVC (H.265), which Chrome, Firefox and
 * Edge cannot decode — the video plays audio over a black frame, and the grid tile has no thumbnail
 * because that thumbnail is a client-side first-frame decode. Safari *can* decode HEVC, so the bug
 * is invisible on an iPad or a Mac.
 *
 * **The container tells you nothing.** `.mov` and `.mp4` are both ISOBMFF and either can hold
 * either codec, so `format`/`type`/the file extension cannot answer this question — not even in
 * principle. The only reliable source is the sample-entry fourcc in the box tree:
 *
 *   `moov > trak > mdia > minf > stbl > stsd > <fourcc>`
 *
 * where `avc1`/`avc3` mean H.264, `hvc1`/`hev1` mean HEVC, and `vp09`/`ap4h`/… mean something else
 * again. We read the raw fourcc rather than a normalized codec name so callers can distinguish
 * cases that matter (`hvc1` vs `hev1` differ only in where parameter sets live).
 *
 * Why parse bytes instead of asking a service:
 * - On the upload path the browser already holds the `File`, so reading any part of it is free.
 * - On the migration path `handleVideo` already downloads the whole buffer.
 * - AWS MediaConvert's `Probe` API would also work, but it needs `@aws-sdk/client-mediaconvert`
 *   (>= 3.759.0, vs the 3.495.0 installed here) and it only reports `HEVC`/`AVC`, not the fourcc.
 * - Probing a Cloudinary *delivery* URL is actively wrong: Cloudinary transcodes on delivery, so a
 *   delivery URL reports H.264 even when the stored original is HEVC.
 *
 * Reading bytes we are about to store is authoritative by construction — it describes the exact
 * object, not a rendition of it.
 *
 * Note on box placement: camera originals put `moov` at the *end* of the file (iOS
 * `shouldOptimizeForNetworkUse` defaults false; Android only reserves room for a leading `moov`
 * when the recorder set a max file size >= 5 MB). This module never assumes `moov` is near the
 * front — it walks the top-level box chain by size, reading only 16 bytes per hop, so it finds
 * `moov` wherever it sits without ever reading `mdat`.
 */

/** Bytes needed to read a box header: 4 size + 4 type, plus 8 more for a 64-bit `largesize`. */
const BOX_HEADER_MAX = 16

/**
 * Codec fourccs that every browser we support can decode. Everything else gets transcoded.
 *
 * This is deliberately an **allow-list, not a deny-list of HEVC**. Measuring the production Cloudinary
 * library (11,406 videos, 16.0% not H.264) turned up vp9, h263 and prores in the tail alongside HEVC.
 * A deny-list of `hvc1`/`hev1` would have shipped a fix that silently missed all three — and did: the
 * first version of the audit script used one and under-reported the affected share.
 *
 * `avc1` and `avc3` are both H.264 and differ only in whether parameter sets are in-band.
 *
 * Note on vp9 specifically: it **does** play in Chrome, Firefox, Edge and Safari 14+, so leaving it out
 * is a conservative choice rather than a correctness requirement. It is a handful of assets out of
 * ~550 sampled, so transcoding them costs almost nothing and keeps the rule simple — but do not read
 * its presence here as "these videos are broken". `h263` and `prores` genuinely do not play.
 */
export const PLAYABLE_VIDEO_CODECS = ['avc1', 'avc3']

/** Fourccs known to mean HEVC, kept separate for reporting/metrics rather than for the decision. */
export const HEVC_VIDEO_CODECS = ['hvc1', 'hev1', 'dvh1', 'dvhe']

export interface VideoCodecInfo {
  /** The raw sample-entry fourcc, lowercased — e.g. `avc1`, `hvc1`, `vp09`. */
  codec: string
  /** Pixel width from the video sample entry, when present. */
  width?: number
  /** Pixel height from the video sample entry, when present. */
  height?: number
}

/**
 * A byte source this module can walk. Both shapes are already available at the two call sites, so
 * neither caller has to buffer a whole video just to read a fourcc:
 * - a browser `Blob`/`File` (the upload path), read lazily via `slice`
 * - an in-memory `Uint8Array`/`Buffer` (the migration path, which already fetched the bytes)
 *
 * Typed as `ArrayBufferView` rather than `Uint8Array` deliberately. TypeScript 5.7 made `Uint8Array`
 * generic over its backing buffer, which stops Node's `Buffer` assigning to a bare
 * `Uint8Array<ArrayBufferLike>` parameter. `ArrayBufferView` covers `Buffer`, `Uint8Array` and any
 * other view without dragging that generic through every call site, and normalizing it below is free
 * (a view over the same memory, not a copy).
 */
export type VideoByteSource = ArrayBufferView | Blob

type NormalizedSource = { kind: 'bytes'; bytes: Uint8Array } | { kind: 'blob'; blob: Blob }

/**
 * Detect a Blob by capability rather than `instanceof`.
 *
 * `instanceof Blob` is unreliable here. It is false whenever the value and the `Blob` global come from
 * different realms — which happens in test environments (a `File` built against one realm's `Blob`),
 * and can happen with polyfills or bundler-injected globals. A `File` of 2262 bytes reporting
 * `instanceof Blob === false` is exactly how this was found, and silently falling through to the byte
 * path would have made every video look undetectable.
 *
 * `arrayBuffer` plus a numeric `size` separates a Blob from a `Uint8Array`, which also has `slice` but
 * has neither of those.
 */
function isBlobLike(source: unknown): source is Blob {
  const candidate = source as Blob | undefined
  return (
    !!candidate &&
    typeof (candidate as Blob).slice === 'function' &&
    typeof (candidate as Blob).arrayBuffer === 'function' &&
    typeof (candidate as Blob).size === 'number'
  )
}

/** Present any accepted source in a uniform shape, without copying bytes. */
function normalize(source: VideoByteSource): NormalizedSource {
  if (isBlobLike(source)) return { kind: 'blob', blob: source }
  const view = source as ArrayBufferView
  return { kind: 'bytes', bytes: new Uint8Array(view.buffer, view.byteOffset, view.byteLength) }
}

function sourceSize(source: NormalizedSource): number {
  return source.kind === 'bytes' ? source.bytes.byteLength : source.blob.size
}

async function readRange(source: NormalizedSource, start: number, end: number): Promise<Uint8Array> {
  const size = sourceSize(source)
  const from = Math.max(0, Math.min(start, size))
  const to = Math.max(from, Math.min(end, size))
  if (source.kind === 'bytes') {
    return source.bytes.subarray(from, to)
  }
  return new Uint8Array(await source.blob.slice(from, to).arrayBuffer())
}

/** Read a big-endian uint32. */
function u32(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0
}

/** Read a big-endian uint16. */
function u16(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] << 8) | bytes[offset + 1]) >>> 0
}

/** Read a 4-character box type, lowercased. Non-printable bytes make this return ''. */
function fourcc(bytes: Uint8Array, offset: number): string {
  let out = ''
  for (let i = 0; i < 4; i++) {
    const code = bytes[offset + i]
    // Anything outside printable ASCII means we are not looking at a real box header.
    if (code < 0x20 || code > 0x7e) return ''
    out += String.fromCharCode(code)
  }
  return out.toLowerCase()
}

interface BoxHeader {
  type: string
  /** Offset of the box's payload, absolute within the source. */
  contentStart: number
  /** Offset one past the box's last byte, absolute within the source. */
  end: number
}

/**
 * Parse the box header at `offset`. Handles both extended-size forms from ISO/IEC 14496-12:
 * `size === 1` means a 64-bit `largesize` follows the type, and `size === 0` means the box runs to
 * the end of the file. Returns undefined for a malformed or truncated header.
 */
function parseHeader(header: Uint8Array, offset: number, sourceEnd: number): BoxHeader | undefined {
  if (header.byteLength < 8) return undefined
  const type = fourcc(header, 4)
  if (!type) return undefined

  const size = u32(header, 0)
  if (size === 1) {
    if (header.byteLength < 16) return undefined
    // 64-bit largesize. Read as two 32-bit halves; anything needing more than 53 bits of precision
    // is far past any real video, so clamp to the source end rather than risk a wrapped value.
    const high = u32(header, 8)
    const low = u32(header, 12)
    const large = high * 0x100000000 + low
    if (large < 16) return undefined
    return { type, contentStart: offset + 16, end: Math.min(offset + large, sourceEnd) }
  }
  if (size === 0) {
    // Box extends to end of file.
    return { type, contentStart: offset + 8, end: sourceEnd }
  }
  if (size < 8) return undefined
  return { type, contentStart: offset + 8, end: Math.min(offset + size, sourceEnd) }
}

/**
 * Walk the top-level box chain to find `moov`, reading only a header at a time.
 *
 * This is the part that makes a tail-placed `moov` a non-issue: we never read `mdat`, we just hop
 * over it using its declared size. For a `Blob` that is one 16-byte `slice` per top-level box.
 */
async function findBox(
  source: NormalizedSource,
  type: string,
  start: number,
  end: number
): Promise<BoxHeader | undefined> {
  let offset = start
  // A malformed file could in principle declare zero-advance boxes; the header guards below reject
  // size < 8, so every iteration advances by at least 8 bytes and this terminates.
  while (offset + 8 <= end) {
    const header = await readRange(source, offset, Math.min(offset + BOX_HEADER_MAX, end))
    const box = parseHeader(header, offset, end)
    if (!box) return undefined
    if (box.type === type) return box
    if (box.end <= offset) return undefined
    offset = box.end
  }
  return undefined
}

/** Find a box within an already-buffered container payload. Offsets are relative to `bytes`. */
function findBoxInBuffer(bytes: Uint8Array, type: string, start = 0, end = bytes.byteLength): BoxHeader | undefined {
  let offset = start
  while (offset + 8 <= end) {
    const box = parseHeader(bytes.subarray(offset, Math.min(offset + BOX_HEADER_MAX, end)), offset, end)
    if (!box) return undefined
    if (box.type === type) return box
    if (box.end <= offset) return undefined
    offset = box.end
  }
  return undefined
}

/** Collect every box of a given type at one level. Used for `trak`, since a file has several. */
function findAllBoxesInBuffer(bytes: Uint8Array, type: string, start = 0, end = bytes.byteLength): BoxHeader[] {
  const found: BoxHeader[] = []
  let offset = start
  while (offset + 8 <= end) {
    const box = parseHeader(bytes.subarray(offset, Math.min(offset + BOX_HEADER_MAX, end)), offset, end)
    if (!box) break
    if (box.type === type) found.push(box)
    if (box.end <= offset) break
    offset = box.end
  }
  return found
}

/**
 * Read the handler type from `mdia > hdlr`. `vide` marks the video track.
 *
 * We select the track by handler rather than by guessing from the fourcc, so an audio-only or
 * subtitle track can never be mistaken for the video track.
 */
function handlerType(moov: Uint8Array, mdia: BoxHeader): string | undefined {
  const hdlr = findBoxInBuffer(moov, 'hdlr', mdia.contentStart, mdia.end)
  if (!hdlr) return undefined
  // hdlr is a FullBox: 4 bytes version/flags, then 4 bytes pre_defined, then the 4-byte handler type.
  const at = hdlr.contentStart + 8
  if (at + 4 > hdlr.end) return undefined
  return fourcc(moov, at)
}

/**
 * Read the first sample entry from `stbl > stsd`.
 *
 * `stsd` is a FullBox followed by a 4-byte entry count, then the entries themselves. Each entry is
 * an ordinary box whose *type* is the codec fourcc. For a video entry the payload is a
 * `VisualSampleEntry`, which carries width and height 24 bytes in.
 */
function readSampleEntry(moov: Uint8Array, stbl: BoxHeader): VideoCodecInfo | undefined {
  const stsd = findBoxInBuffer(moov, 'stsd', stbl.contentStart, stbl.end)
  if (!stsd) return undefined

  // 4 bytes version/flags + 4 bytes entry_count.
  const entriesStart = stsd.contentStart + 8
  if (entriesStart + 8 > stsd.end) return undefined

  const entry = parseHeader(
    moov.subarray(entriesStart, Math.min(entriesStart + BOX_HEADER_MAX, stsd.end)),
    entriesStart,
    stsd.end
  )
  if (!entry) return undefined

  const info: VideoCodecInfo = { codec: entry.type }

  // VisualSampleEntry layout after the box header: 6 reserved + 2 data_reference_index +
  // 2 pre_defined + 2 reserved + 12 pre_defined = 24 bytes, then width (2) and height (2).
  const dimsAt = entry.contentStart + 24
  if (dimsAt + 4 <= entry.end) {
    const width = u16(moov, dimsAt)
    const height = u16(moov, dimsAt + 2)
    if (width > 0 && height > 0) {
      info.width = width
      info.height = height
    }
  }

  return info
}

/**
 * Detect the video codec of an ISOBMFF file.
 *
 * Returns `undefined` when the codec cannot be determined — a non-ISOBMFF file, a truncated
 * download, an audio-only file, or a malformed box tree. Callers must treat `undefined` as "do not
 * know", never as "needs transcoding": see `needsTranscode`, which fails safe in that case.
 */
export async function detectVideoCodec(input: VideoByteSource): Promise<VideoCodecInfo | undefined> {
  const source = normalize(input)
  const size = sourceSize(source)
  if (size < 8) return undefined

  const moovHeader = await findBox(source, 'moov', 0, size)
  if (!moovHeader) return undefined

  // Read **only the `moov` payload**, not `0..moovHeader.end`.
  //
  // This is load-bearing, not a micro-optimisation. Camera originals put `moov` at the *end* of the
  // file, so `moovHeader.end` is effectively the file size — reading from 0 would copy the entire
  // multi-gigabyte recording into memory, in the browser, before the upload even starts. On iPad that
  // is the WebKit out-of-memory path. `moov` itself is roughly 1 KB per second of video, so this reads
  // kilobytes regardless of how large the file is.
  //
  // The consequence is that every offset below is **relative to the payload**, not absolute within the
  // file. The in-buffer helpers all default to the whole buffer, which keeps that consistent — do not
  // reintroduce absolute file offsets here.
  const moovPayload = await readRange(source, moovHeader.contentStart, moovHeader.end)

  for (const trak of findAllBoxesInBuffer(moovPayload, 'trak')) {
    const mdia = findBoxInBuffer(moovPayload, 'mdia', trak.contentStart, trak.end)
    if (!mdia) continue
    if (handlerType(moovPayload, mdia) !== 'vide') continue

    const minf = findBoxInBuffer(moovPayload, 'minf', mdia.contentStart, mdia.end)
    if (!minf) continue
    const stbl = findBoxInBuffer(moovPayload, 'stbl', minf.contentStart, minf.end)
    if (!stbl) continue

    const info = readSampleEntry(moovPayload, stbl)
    if (info) return info
  }

  return undefined
}

/**
 * Whether a video needs transcoding to play everywhere.
 *
 * Fails **safe**: an unknown or undetectable codec returns `false`. A detection outage must not
 * stampede every upload into a paid transcode, and leaving a file alone is exactly today's
 * behaviour — so the worst case of a wrong `false` is the status quo, while the worst case of a
 * wrong `true` is a bill and a needlessly re-encoded original.
 */
export function needsTranscode(codec?: string): boolean {
  if (!codec) return false
  return !PLAYABLE_VIDEO_CODECS.includes(codec.toLowerCase())
}

/**
 * Whether a codec is *positively confirmed* playable everywhere.
 *
 * This is **not** the inverse of `needsTranscode`. Both return false for an unknown codec, but they
 * fail in deliberately different directions, and using the wrong one is a real bug:
 *
 * - `needsTranscode` answers "should I spend effort on this?" — unknown means leave it alone.
 * - `isPlayableVideoCodec` answers "may I trust these bytes?" — unknown means no.
 *
 * The distinction matters when validating something we just fetched. Cloudinary answers a request
 * for a rendition it has not generated yet with `423` and a zero-byte `image/gif` body. That body
 * has no detectable codec, so `!needsTranscode(undefined)` is true and would wave it through, and we
 * would store an empty file in place of a customer's video. This predicate rejects it.
 */
export function isPlayableVideoCodec(codec?: string): boolean {
  return !!codec && PLAYABLE_VIDEO_CODECS.includes(codec.toLowerCase())
}

/** Whether a fourcc is one of the HEVC variants. For reporting; the decision uses `needsTranscode`. */
export function isHevcCodec(codec?: string): boolean {
  return !!codec && HEVC_VIDEO_CODECS.includes(codec.toLowerCase())
}

export default detectVideoCodec
